import React, { useEffect, useState, useCallback } from 'react'
import { Card, Badge, Row, Col, Button, Spinner } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faPaperPlane,
  faExclamationTriangle,
  faClock,
  faEye,
  faInbox,
  faPhone,
  faIdBadge,
  faSyncAlt,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'

import PacienteService from '../../../../services/paciente.service'
import Paginator from '../../../Common/Paginator'

const PAGE_SIZE = 10

const STATUS_META = {
  pending: { label: 'Aguardando fila', variant: 'secondary', icon: faClock },
  queued: { label: 'Na fila', variant: 'info', icon: faInbox },
  sent: { label: 'Enviada (WABA)', variant: 'primary', icon: faPaperPlane },
  enqueued: { label: 'Enfileirada (Gupshup)', variant: 'info', icon: faInbox },
  delivered: { label: 'Entregue', variant: 'success', icon: faCheckCircle },
  read: { label: 'Visualizada', variant: 'success', icon: faEye },
  failed: { label: 'Falhou', variant: 'danger', icon: faExclamationTriangle },
}

function formatDateTime(value) {
  if (!value) return null
  const m = moment(value)
  if (!m.isValid()) return null
  return m.format('DD/MM/YYYY HH:mm:ss')
}

function relative(value) {
  if (!value) return null
  const m = moment(value)
  return m.isValid() ? m.fromNow() : null
}

function getStatusMeta(status) {
  const key = (status || '').toLowerCase()
  return STATUS_META[key] || { label: status || 'Desconhecido', variant: 'secondary', icon: faClock }
}

function StatusBadge({ status }) {
  const meta = getStatusMeta(status)
  return (
    <Badge variant={meta.variant} className='d-inline-flex align-items-center px-2 py-1' style={{ fontSize: '0.78rem', fontWeight: 500, letterSpacing: '0.01em' }}>
      <FontAwesomeIcon icon={meta.icon} className='mr-1' />
      {meta.label}
    </Badge>
  )
}

function TimelineStep({ icon, label, value, danger }) {
  const dateLabel = formatDateTime(value)
  const rel = relative(value)
  return (
    <div className='d-flex align-items-start py-1' style={{ gap: 8 }}>
      <div
        className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
        style={{
          width: 26,
          height: 26,
          background: danger ? '#fbe9e7' : value ? '#e8f3fd' : '#f1f3f5',
          color: danger ? '#c62828' : value ? '#0881c7' : '#adb5bd',
          fontSize: '0.75rem',
        }}
      >
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className='small flex-grow-1 min-w-0'>
        <div className='text-muted' style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </div>
        {dateLabel ? (
          <div className='text-dark' style={{ lineHeight: 1.25 }}>
            <span className='font-weight-500' style={{ fontWeight: 500 }}>{dateLabel}</span>
            {rel && <span className='text-muted ml-2' style={{ fontSize: '0.72rem' }}>({rel})</span>}
          </div>
        ) : (
          <div className='text-muted'>—</div>
        )}
      </div>
    </div>
  )
}

function ErrorBlock({ mensagem }) {
  if (!mensagem.error && !mensagem.failureReason && !mensagem.failureCode) return null

  return (
    <div
      className='mt-3 p-2 rounded'
      style={{ background: '#fff5f5', border: '1px solid #f8d7da', color: '#842029' }}
    >
      <div className='d-flex align-items-center mb-1' style={{ fontWeight: 600, fontSize: '0.82rem' }}>
        <FontAwesomeIcon icon={faExclamationTriangle} className='mr-2' />
        Falha no envio
        {mensagem.failureCode && (
          <Badge variant='light' className='ml-2 border' style={{ fontSize: '0.7rem' }}>
            #{mensagem.failureCode}
          </Badge>
        )}
      </div>
      {mensagem.failureReason && (
        <div className='small' style={{ lineHeight: 1.35 }}>
          {mensagem.failureReason}
        </div>
      )}
      {mensagem.error && mensagem.error !== mensagem.failureReason && (
        <div className='small text-muted mt-1' style={{ lineHeight: 1.3 }}>
          <strong className='text-secondary'>Detalhe técnico: </strong>
          {mensagem.error}
        </div>
      )}
    </div>
  )
}

function MessageCard({ mensagem }) {
  const meta = getStatusMeta(mensagem.status)
  const isFailed = (mensagem.status || '').toLowerCase() === 'failed'
  const headerDate = formatDateTime(mensagem.sendAt) || formatDateTime(mensagem.createdAt) || '—'

  const borderColor = isFailed ? '#f5c2c7' : meta.variant === 'success' ? '#c3e6cb' : '#dee2e6'

  return (
    <Card className='mb-3 shadow-sm' style={{ border: `1px solid ${borderColor}`, borderRadius: 10 }}>
      <Card.Body className='p-3 p-md-4'>
        <div className='d-flex justify-content-between align-items-start flex-wrap' style={{ gap: 8 }}>
          <div className='d-flex align-items-center flex-wrap' style={{ gap: 8 }}>
            <StatusBadge status={mensagem.status} />
            {mensagem.templateId && (
              <Badge variant='light' className='border' style={{ fontSize: '0.7rem' }}>
                Template: {mensagem.templateId}
              </Badge>
            )}
            {mensagem.by && (
              <Badge variant='light' className='border' style={{ fontSize: '0.7rem' }}>
                Origem: {mensagem.by}
              </Badge>
            )}
          </div>
          <div className='text-muted small text-right' style={{ minWidth: 0 }}>
            {headerDate}
          </div>
        </div>

        <div
          className='mt-3 p-3 rounded'
          style={{ background: '#f8f9fa', whiteSpace: 'pre-wrap', fontSize: '0.92rem', lineHeight: 1.45 }}
        >
          {mensagem.mensagem || <span className='text-muted'>(sem corpo de mensagem)</span>}
        </div>

        <Row className='mt-3'>
          <Col xs='12' md='6'>
            <TimelineStep icon={faClock} label='Criada / Enviada' value={mensagem.sendAt || mensagem.createdAt} />
            <TimelineStep icon={faInbox} label='Confirmada na fila (SQS)' value={mensagem.queuedAt} />
            <TimelineStep icon={faPaperPlane} label='Processada (Gupshup)' value={mensagem.receiveAt} />
          </Col>
          <Col xs='12' md='6'>
            <TimelineStep icon={faEye} label='Visualizada' value={mensagem.seenAt} />
            <TimelineStep icon={faExclamationTriangle} label='Falhou em' value={mensagem.failedAt} danger />
            <TimelineStep icon={faSyncAlt} label='Última atualização' value={mensagem.updatedAt} />
          </Col>
        </Row>

        <div className='mt-2 small text-muted d-flex flex-wrap' style={{ gap: 12 }}>
          {mensagem.destination && (
            <span>
              <FontAwesomeIcon icon={faPhone} className='mr-1' />
              {mensagem.destination}
            </span>
          )}
          {mensagem.gsId && (
            <span title='ID retornado pela Gupshup/WABA'>
              <FontAwesomeIcon icon={faIdBadge} className='mr-1' />
              gsId: {mensagem.gsId}
            </span>
          )}
        </div>

        <ErrorBlock mensagem={mensagem} />
      </Card.Body>
    </Card>
  )
}

export default function HistoricoMensagens({ pacienteId }) {
  const [pagina, setPagina] = useState(1)
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchData = useCallback(
    async (paginaSolicitada) => {
      if (!pacienteId) return
      setIsLoading(true)
      setError(null)
      try {
        const resp = await PacienteService.getHistoricoMensagens(pacienteId, paginaSolicitada, PAGE_SIZE)
        setData(resp?.data || null)
      } catch (e) {
        setError('Não foi possível carregar o histórico de mensagens.')
        setData(null)
      } finally {
        setIsLoading(false)
      }
    },
    [pacienteId]
  )

  useEffect(() => {
    fetchData(pagina)
  }, [fetchData, pagina])

  const handlePagina = ({ selected }) => {
    setPagina(selected + 1)
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const totalItems = data?.totalItems ?? 0
  const totalPages = data?.totalPages ?? 0
  const mensagens = data?.mensagens || []

  const renderResumo = () => {
    if (!data || !data.conversationId) return null
    return (
      <Card className='mb-3 border-0 shadow-sm' style={{ borderRadius: 10 }}>
        <Card.Body className='p-3'>
          <div className='d-flex flex-wrap justify-content-between align-items-center' style={{ gap: 12 }}>
            <div className='small'>
              <div className='text-muted' style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Conversa
              </div>
              <div className='text-dark font-weight-500' style={{ fontWeight: 500 }}>
                {data.recipientName || '—'}
                {data.recipientPhone && <span className='text-muted ml-2'>{data.recipientPhone}</span>}
              </div>
            </div>
            <div className='small text-right'>
              <div className='text-muted' style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Última mensagem
              </div>
              <div className='text-dark'>{formatDateTime(data.lastMessage) || '—'}</div>
            </div>
            <div className='small text-right'>
              <div className='text-muted' style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Total
              </div>
              <div className='text-dark font-weight-500' style={{ fontWeight: 500 }}>
                {totalItems} mensagem{totalItems === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    )
  }

  const renderEmpty = () => {
    if (isLoading) return null
    if (mensagens.length > 0) return null
    if (data && data.conversationId) {
      return (
        <div className='text-center text-muted py-5'>
          Nenhuma mensagem encontrada para esta página.
        </div>
      )
    }
    return (
      <div className='text-center text-muted py-5'>
        <FontAwesomeIcon icon={faInbox} className='mb-2' style={{ fontSize: '1.8rem', color: '#adb5bd' }} />
        <div>Nenhuma conversa registrada para este paciente.</div>
      </div>
    )
  }

  return (
    <div className='py-2'>
      <div className='d-flex justify-content-between align-items-center mb-3 flex-wrap' style={{ gap: 8 }}>
        <h5 className='m-0 text-secondary font-weight-bold'>Histórico de mensagens</h5>
        <Button
          variant='outline-secondary'
          size='sm'
          onClick={() => fetchData(pagina)}
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faSyncAlt} className='mr-1' spin={isLoading} />
          Atualizar
        </Button>
      </div>

      {error && (
        <div className='alert alert-danger small py-2' role='alert'>
          {error}
        </div>
      )}

      {renderResumo()}

      {isLoading && mensagens.length === 0 ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner animation='border' variant='primary' />
        </div>
      ) : (
        <>
          {mensagens.map((m) => (
            <MessageCard key={m.id} mensagem={m} />
          ))}
          {renderEmpty()}
        </>
      )}

      {totalPages > 1 && (
        <div className='d-flex justify-content-center mt-3'>
          <Paginator
            pageCount={totalPages}
            paginaAtual={Math.max(0, (data?.page || 1) - 1)}
            handlePagina={handlePagina}
          />
        </div>
      )}

      {totalPages <= 1 && mensagens.length > 0 && (
        <div className='text-center text-muted small mt-2'>
          Mostrando {mensagens.length} de {totalItems}
        </div>
      )}

      <style>{`
        .historico-mensagens-fade-enter {
          opacity: 0;
        }
      `}</style>

      <NavigationHint paginaAtual={data?.page || pagina} totalPages={totalPages} />
    </div>
  )
}

function NavigationHint({ paginaAtual, totalPages }) {
  if (!totalPages || totalPages <= 1) return null
  return (
    <div className='text-center small text-muted mt-2'>
      <FontAwesomeIcon icon={faChevronLeft} className='mr-1' />
      Página {paginaAtual} de {totalPages}
      <FontAwesomeIcon icon={faChevronRight} className='ml-1' />
    </div>
  )
}
